import { useEffect, useMemo, useSyncExternalStore } from "react";
import type { ModifierGeometry } from "./modifiers/modifierTypes.ts";

export interface MannequinSurfaceSnapshot {
  objectId: string;
  sourceKey: string;
  cacheKey: string;
  geometry: ModifierGeometry;
}

const snapshots = new Map<string, MannequinSurfaceSnapshot>();
const demandCounts = new Map<string, number>();
const listeners = new Set<() => void>();
let version = 0;

function emitChange(): void {
  version++;
  for (const listener of listeners) listener();
}

function normalizedObjectIds(objectIds: readonly string[]): string[] {
  return Array.from(new Set(objectIds.filter((id) => id.length > 0))).sort();
}

export function subscribeMannequinSurfaceSnapshots(
  listener: () => void,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getMannequinSurfaceSnapshotVersion(): number {
  return version;
}

export function getMannequinSurfaceSnapshot(
  objectId: string,
): MannequinSurfaceSnapshot | null {
  return snapshots.get(objectId) ?? null;
}

export function publishMannequinSurfaceSnapshot(
  objectId: string,
  sourceKey: string,
  geometry: ModifierGeometry,
): void {
  snapshots.set(objectId, {
    objectId,
    sourceKey,
    cacheKey: `mannequin-surface:${objectId}:${sourceKey}`,
    geometry,
  });
  emitChange();
}

export function clearMannequinSurfaceSnapshot(objectId: string): void {
  if (!snapshots.delete(objectId)) return;
  emitChange();
}

export function registerMannequinSurfaceDemand(objectId: string): () => void {
  const prev = demandCounts.get(objectId) ?? 0;
  demandCounts.set(objectId, prev + 1);
  emitChange();

  let released = false;
  return () => {
    if (released) return;
    released = true;
    const count = demandCounts.get(objectId) ?? 0;
    if (count <= 1) {
      demandCounts.delete(objectId);
      snapshots.delete(objectId);
    } else {
      demandCounts.set(objectId, count - 1);
    }
    emitChange();
  };
}

export function isMannequinSurfaceDemanded(objectId: string): boolean {
  return (demandCounts.get(objectId) ?? 0) > 0;
}

export function getMannequinSurfaceDemandCount(objectId: string): number {
  return demandCounts.get(objectId) ?? 0;
}

export function useMannequinSurfaceSnapshots(
  objectIds: readonly string[],
): ReadonlyMap<string, MannequinSurfaceSnapshot> {
  const normalizedIds = useMemo(
    () => normalizedObjectIds(objectIds),
    [objectIds],
  );

  useEffect(() => {
    if (normalizedIds.length === 0) return;
    const releases = normalizedIds.map((id) =>
      registerMannequinSurfaceDemand(id),
    );
    return () => {
      for (const release of releases) release();
    };
  }, [normalizedIds]);

  useSyncExternalStore(
    subscribeMannequinSurfaceSnapshots,
    getMannequinSurfaceSnapshotVersion,
    getMannequinSurfaceSnapshotVersion,
  );

  const out = new Map<string, MannequinSurfaceSnapshot>();
  for (const id of normalizedIds) {
    const snapshot = snapshots.get(id);
    if (snapshot) out.set(id, snapshot);
  }
  return out;
}

export function resetMannequinSurfaceSnapshotsForTests(): void {
  snapshots.clear();
  demandCounts.clear();
  emitChange();
}
